import {
  LBTooltip,
  LBTooltipContent,
  LBTooltipTrigger,
  LBBadge,
  LBGithubIcon,
  LBJiraIcon,
  LBSlackIcon,
  LBGitlabIcon,
  LBBitbucketIcon,
  LBAzureIcon,
  LBAzureBoardsIcon,
  LBCopilotIcon,
  LBCursorIcon,
  LBClaudeIcon,
  LBCodexIcon,
  LBDevinIcon,
  LBAmazonQIcon,
  LBKiroIcon,
  LBShortcutIcon,
  LBMsTeamsIcon
} from '@linearb/buzz'
import Saml from '../icons/saml-svgrepo-com.svg?react'
import type { OrgAiIntegration } from '../types'

export type SensorName =
  | 'github'
  | 'github_issues'
  | 'jira'
  | 'slack'
  | 'gitlab'
  | 'bitbucket_server'
  | 'bitbucket'
  | 'saml'
  | 'azure_devops'
  | 'azure_boards'
  | 'azure_ad_oauth2'
  | 'copilot'
  | 'cursor'
  | 'claude'
  | 'codex'
  | 'windsurf'
  | 'amazonq'
  | 'kiro'
  | 'shortcut'
  | 'clubhouse'

interface IconConfig {
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  label: string
}

export const ICONS_CONFIG: Record<SensorName, IconConfig> = {
  github: { icon: LBGithubIcon, label: 'GitHub' },
  github_issues: { icon: LBGithubIcon, label: 'GitHub Issues' },
  jira: { icon: LBJiraIcon, label: 'Jira' },
  slack: { icon: LBSlackIcon, label: 'Slack' },
  gitlab: { icon: LBGitlabIcon, label: 'GitLab' },
  bitbucket_server: { icon: LBBitbucketIcon, label: 'Bitbucket Server' },
  bitbucket: { icon: LBBitbucketIcon, label: 'Bitbucket' },
  saml: { icon: Saml, label: 'SAML' },
  azure_devops: { icon: LBAzureIcon, label: 'Azure DevOps' },
  azure_boards: { icon: LBAzureBoardsIcon, label: 'Azure Boards' },
  azure_ad_oauth2: { icon: LBMsTeamsIcon, label: 'Microsoft Teams' },
  copilot: { icon: LBCopilotIcon, label: 'GitHub Copilot' },
  cursor: { icon: LBCursorIcon, label: 'Cursor' },
  claude: { icon: LBClaudeIcon, label: 'Claude' },
  codex: { icon: LBCodexIcon, label: 'Codex' },
  windsurf: { icon: LBDevinIcon, label: 'Devin (Windsurf)' },
  amazonq: { icon: LBAmazonQIcon, label: 'Amazon Q' },
  kiro: { icon: LBKiroIcon, label: 'Kiro' },
  shortcut: { icon: LBShortcutIcon, label: 'Shortcut' },
  clubhouse: { icon: LBShortcutIcon, label: 'Shortcut (Clubhouse)' }
}

// Canonical display label for a sensor/tool (falls back to the raw name)
export const getSensorLabel = (name?: string): string =>
  (name && ICONS_CONFIG[name.toLowerCase() as SensorName]?.label) || name || ''

// Default sensor names repeat the provider, optionally with a counter or version
// suffix ('Gitlab 3', 'Azure_ad_oauth2_v2'); anything else is a name the customer set
const baseName = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/(v?\d+)+$/, '')

export const getCustomSensorName = (
  name?: string,
  sensorName?: string
): string | null =>
  name &&
  ![sensorName, getSensorLabel(sensorName)]
    .filter(Boolean)
    .some(v => baseName(v) === baseName(name))
    ? name
    : null

// Bare sensor/tool icon (no tooltip), for rows that already show the label.
export const SensorGlyph: React.FC<{ name?: string; className?: string }> = ({
  name,
  className
}) => {
  const config = name && ICONS_CONFIG[name.toLowerCase() as SensorName]
  if (!config) {
    return null
  }
  const IconComponent = config.icon
  return <IconComponent className={className} />
}

// Tools that report interaction counters (chat messages, inline suggestions/
// acceptances) rather than cost/token metrics. Kiro also has cost, but still
// writes this counter set — so this selects the activity-metric set, not billing.
export const usesAdoptionKeys = (tool?: string | null): boolean =>
  ['kiro', 'amazonq'].includes((tool ?? '').toLowerCase())

// AI integration method labels mirror linweb's AI-Integrations settings UI so the
// backoffice matches the product (ClaudeCodeSetting/constants.ts, connectionTypeLabels.ts)
export const CLAUDE_METHOD_LABEL: Record<string, string> = {
  admin_api: 'Admin API',
  analytics_api: 'Analytics API',
  otel: 'OpenTelemetry'
}
export const CODEX_METHOD_LABEL: Record<string, string> = {
  platform_admin_api: 'Admin API',
  analytics_api: 'Analytics API',
  otel: 'OpenTelemetry'
}
export const WINDSURF_METHOD_LABEL: Record<string, string> = {
  analytics_api: 'Analytics API',
  devin_api: 'Devin API'
}
export const COPILOT_AUTH_TYPE_LABEL: Record<string, string> = {
  classic: 'Classic token',
  fine_grained: 'Fine-grained token',
  enterprise: 'Enterprise',
  github_app: 'GitHub App'
}
// Git sensor auth strategy, mirrors linrest getAuthStrategy
export const GIT_AUTH_STRATEGY_LABEL: Record<string, string> = {
  github_app: 'GitHub App',
  pat: 'Personal token',
  oauth: 'OAuth'
}
// Copilot org-collection strategy chosen during install (aiMetrics setting)
export const COPILOT_SELECTION_TYPE_LABEL: Record<string, string> = {
  all: 'All organizations',
  custom: 'Custom',
  regex: 'Regex'
}

// Only Claude, Codex, Windsurf, and Copilot expose a method; others are single-method
export const getAiMethodLabel = (ai: OrgAiIntegration): string | null => {
  const tool = ai.tool?.toLowerCase()
  if (tool === 'claude' && ai.collection_method) {
    return CLAUDE_METHOD_LABEL[ai.collection_method] || null
  }
  if (tool === 'codex' && ai.collection_method) {
    return CODEX_METHOD_LABEL[ai.collection_method] || null
  }
  if (tool === 'windsurf' && ai.collection_method) {
    return WINDSURF_METHOD_LABEL[ai.collection_method] || null
  }
  if (tool === 'copilot' && ai.auth_type) {
    return COPILOT_AUTH_TYPE_LABEL[ai.auth_type] || null
  }
  return null
}

// Map a tool's raw method keys to their display labels (Claude / Codex / Windsurf / Copilot only)
const methodLabelsFor = (name: SensorName, methods?: string[]): string[] => {
  if (!methods?.length) {
    return []
  }
  const map =
    name === 'claude'
      ? CLAUDE_METHOD_LABEL
      : name === 'codex'
        ? CODEX_METHOD_LABEL
        : name === 'windsurf'
          ? WINDSURF_METHOD_LABEL
          : name === 'copilot'
            ? COPILOT_AUTH_TYPE_LABEL
            : null
  if (!map) {
    return []
  }
  return methods.map(m => map[m]).filter(Boolean)
}

interface SensorsIconProps {
  name: SensorName
  size?: number
  methods?: string[]
  customDomains?: string[]
}

const SensorsIcon: React.FC<SensorsIconProps> = ({
  name,
  size = 20,
  methods,
  customDomains
}) => {
  const config = ICONS_CONFIG[name]
  if (!config) {
    return null
  }

  const IconComponent = config.icon
  const methodLabels = methodLabelsFor(name, methods)
  const parts = [...methodLabels, ...(customDomains?.filter(Boolean) ?? [])]
  const tooltip = parts.length
    ? `${config.label} · ${parts.join(' · ')}`
    : config.label

  return (
    <LBTooltip>
      <LBTooltipTrigger asChild>
        <span
          className='inline-flex items-center justify-center cursor-default'
          style={{ width: size, height: size, minWidth: size }}
        >
          <IconComponent
            style={{
              width: '100%',
              height: '100%',
              maxWidth: size,
              maxHeight: size
            }}
          />
        </span>
      </LBTooltipTrigger>
      <LBTooltipContent>
        <p>{tooltip}</p>
      </LBTooltipContent>
    </LBTooltip>
  )
}

// Shared provider row (icon + name + method badge) so every AI surface matches.
export const AiProviderLabel: React.FC<{
  tool?: string | null
  method?: string | null
  size?: number
}> = ({ tool, method, size = 18 }) => {
  if (!tool) {
    return null
  }
  return (
    <span className='flex items-center gap-2 min-w-0'>
      <SensorsIcon name={tool.toLowerCase() as SensorName} size={size} />
      <span className='capitalize truncate'>{getSensorLabel(tool)}</span>
      {method && (
        <LBBadge
          variant='secondary'
          className='text-xs px-2 py-0.5 shrink-0 font-normal'
        >
          {method}
        </LBBadge>
      )}
    </span>
  )
}

export default SensorsIcon

export const creditsLabel = (unit?: string | null): string =>
  unit === 'acus' ? 'ACUs' : 'Credits'

// Mid-sentence form: 'credits' lowercased, the ACUs acronym kept
export const creditsWord = (unit?: string | null): string =>
  unit === 'acus' ? 'ACUs' : 'credits'
